import { createApi } from '@reduxjs/toolkit/query/react';
import { gql } from 'graphql-request';
import { baseQuery } from '@classity-edtech/ui-base';
import { PRODUCT_EVENT } from '../store/actionTypes';
import { ProductEvent } from './models/productEvents';

const query = `
  description {
    text
    language
  }
  name {
    displayName
    language
  }
  recurringEventId
  prices {
    currencies {
      amount
      code
      amountInUnit
      id
    }
    description {
      language
      text
    }
    id
    productId
    split
  }
  organizer {
    profile {
      firstname
      lastname
      avatar {
        contentUrl
        medium
      }
      bio
      certifications {
        category
        countryOfOrigin
        periodOfStudyFrom
        periodOfStudyTo
        issuedByName
      }
      experiences {
        from
        to
        organizationId
      }
      preference {
        gradeLevels
      }
    }
    preferences {
      knowsLanguages
    }
  }
`;

export const productEvents = createApi({
  reducerPath: PRODUCT_EVENT,
  baseQuery,
  tagTypes: ['ProductEvent'],
  endpoints: builder => ({
    productEvent: builder.query<ProductEvent, { code: string }>({
      query: variables => ({
        document: gql`
          query GetProductEventByCode($code: String) {
            getProductEventByCode(code: $code) {
              ${query}
            }
          }
        `,
        variables,
      }),
      transformResponse: (response: any) => response?.getProductEventByCode,
    }),
    productEventBySlug: builder.query<ProductEvent, { slug: string }>({
      query: variables => ({
        document: gql`
          query GetProductEventBySlug($slug: String!) {
            getProductEventBySlug(slug: $slug) {
              ${query}
            }
          }
        `,
        variables,
      }),
      transformResponse: (response: any) => response?.getProductEventBySlug,
    }),
    productEvents: builder.query<
      ProductEvent[],
      {
        page?: number;
        limit?: number;
        earliest?: Date | string;
        latest?: Date | string;
        recurringId?: string;
        productEventIds?: string[];
      }
    >({
      query: variables => ({
        document: gql`
          query GetProductEvents(
            $page: Int
            $limit: Int
            $earliest: DateTime
            $latest: DateTime
            $recurringId: String
            $productEventIds: [String!]
          ) {
            getProductEvents(
              page: $page
              limit: $limit
              earliest: $earliest
              latest: $latest
              recurringId: $recurringId
              productEventIds: $productEventIds
            ) {
              calendarId
              prices {
                currencies {
                  amount
                  code
                  amountInUnit
                  id
                }
                description {
                  language
                  text
                }
                id
                productId
                split
              }
              endTime {
                dateTime
                timeZone
              }
              startTime {
                dateTime
                timeZone
              }
              maxAttendees
              location
              id
              code
              category
              description {
                text
                language
              }
              name {
                displayName
                language
              }
              attendees {
                email
                organizer
                user {
                  profile {
                    firstname
                    lastname
                    bio
                    ids {
                      name
                      value
                    }
                    username
                    certifications {
                      category
                      issuedByName
                      countryOfOrigin
                    }
                    avatar {
                      contentUrl
                      medium
                    }
                  }
                  id
                  email
                  country
                  preferences {
                    knowsLanguages
                  }
                }
              }
              isVirtual
              recurringEventId
              lessons {
                participants {
                  userId
                }
              }
            }
          }
        `,
        variables,
      }),
      transformResponse: (response: any) => response?.getProductEvents,
    }),
  }),
});

export const {
  useProductEventsQuery,
  useLazyProductEventsQuery,
  useProductEventQuery,
  useProductEventBySlugQuery,
} = productEvents;
